import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  Chip,
  Divider,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Link as LinkIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { useDatabase } from '../../hooks/useDatabase';

interface Associate {
  name: string;
  title?: string;
  company?: string;
  relationship?: string;
  context?: string;
  source?: string;
  sourceUrl?: string;
  confidence: 'high' | 'medium' | 'low';
}

interface KnownAssociatesProps {
  contactId: string;
  contactName: string;
  contactTitle?: string;
  contactCompany?: string;
  contactIndustry?: string;
}

interface SearchQuery {
  query: string;
  type: 'news' | 'profile' | 'company' | 'industry' | 'social' | 'achievements' | 'speaking' | 'board' | 'investment' | 'background' | 'comprehensive' | 'basic' | 'professional' | 'network' | 'location' | 'role' | 'creative' | 'title' | 'email' | 'description' | 'colleagues';
  reasoning?: string;
}

export const KnownAssociates: React.FC<KnownAssociatesProps> = ({
  contactId,
  contactName,
  contactTitle,
  contactCompany,
  contactIndustry,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [associates, setAssociates] = useState<Associate[]>([]);
  const [summary, setSummary] = useState<string>('');
  const [searchResults, setSearchResults] = useState<{
    newsArticles: number;
    exaResults: number;
  } | null>(null);
  const [searchQueries, setSearchQueries] = useState<SearchQuery[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const db = useDatabase();

  const handleSearch = async () => {
    setLoading(true);
    setError(null);

    try {
      // First, trigger the background search
      const triggerResponse = await fetch('/.netlify/functions/trigger-background-search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contactId,
        }),
      });

      if (!triggerResponse.ok) {
        throw new Error(`Failed to trigger search: ${triggerResponse.status} ${triggerResponse.statusText}`);
      }

      const triggerResult = await triggerResponse.json();
      
      if (triggerResult.error) {
        throw new Error(triggerResult.error);
      }

      // If we got cached results, use them immediately
      if (triggerResult.cached && triggerResult.results) {
        setAssociates(triggerResult.results.associates || []);
        setSummary(triggerResult.results.summary || '');
        setSearchResults(triggerResult.results.searchResults || null);
        setSearchQueries(triggerResult.results.searchQueries || []);
        setHasSearched(true);
        setExpanded(true);
        setLoading(false);
        
        // Trigger networking suggestions recalculation after Known Associates search
        try {
          const { error: recalcError } = await db.recalculateNetworkingSuggestions();
          if (recalcError) {
            console.error('Error recalculating networking suggestions after Known Associates search:', recalcError);
          }
        } catch (err) {
          console.error('Error triggering networking suggestions recalculation:', err);
        }
        
        return;
      }

      // If search is already in progress or just started, poll for results
      if (triggerResult.status === 'processing' || triggerResult.status === 'pending') {
        await pollForResults();
        return;
      }

      // If we got results immediately, use them
      if (triggerResult.results) {
        setAssociates(triggerResult.results.associates || []);
        setSummary(triggerResult.results.summary || '');
        setSearchResults(triggerResult.results.searchResults || null);
        setSearchQueries(triggerResult.results.searchQueries || []);
        setHasSearched(true);
        setExpanded(true);
        
        // Trigger networking suggestions recalculation after Known Associates search
        try {
          const { error: recalcError } = await db.recalculateNetworkingSuggestions();
          if (recalcError) {
            console.error('Error recalculating networking suggestions after Known Associates search:', recalcError);
          }
        } catch (err) {
          console.error('Error triggering networking suggestions recalculation:', err);
        }
      }
      
    } catch (err) {
      console.error('Error searching for associates:', err);
      setError(err instanceof Error ? err.message : 'Failed to search for associates');
    } finally {
      setLoading(false);
    }
  };

  const pollForResults = async () => {
    const maxAttempts = 30; // Poll for up to 5 minutes (30 * 10 seconds)
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(`/.netlify/functions/check-search-status?contactId=${contactId}`);
        
        if (!response.ok) {
          throw new Error(`Status check failed: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.error) {
          throw new Error(result.error);
        }

        if (result.status === 'completed' && result.results) {
          // Search completed successfully
          setAssociates(result.results.associates || []);
          setSummary(result.results.summary || '');
          setSearchResults(result.results.searchResults || null);
          setSearchQueries(result.results.searchQueries || []);
          setHasSearched(true);
          setExpanded(true);
          setLoading(false);
          
          // Trigger networking suggestions recalculation after Known Associates search
          // This will match associates to existing contacts
          try {
            const { error: recalcError } = await db.recalculateNetworkingSuggestions();
            if (recalcError) {
              console.error('Error recalculating networking suggestions after Known Associates search:', recalcError);
            } else {
              console.log('Networking suggestions recalculation triggered after Known Associates search');
            }
          } catch (err) {
            console.error('Error triggering networking suggestions recalculation:', err);
          }
          
          return;
        }

        if (result.status === 'failed') {
          throw new Error(result.error || 'Search failed');
        }

        // If still processing, continue polling
        if (result.status === 'processing' || result.status === 'pending') {
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 10000); // Poll every 10 seconds
          } else {
            throw new Error('Search timed out');
          }
        }

      } catch (err) {
        console.error('Error polling for results:', err);
        setError(err instanceof Error ? err.message : 'Failed to get search results');
        setLoading(false);
      }
    };

    // Start polling
    poll();
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'success';
      case 'medium':
        return 'warning';
      case 'low':
        return 'error';
      default:
        return 'default';
    }
  };

  const getConfidenceIcon = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return <StarIcon fontSize="small" />;
      case 'medium':
        return <StarBorderIcon fontSize="small" />;
      case 'low':
        return <StarBorderIcon fontSize="small" />;
      default:
        return undefined;
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Known Associates</Typography>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
          onClick={handleSearch}
          disabled={loading}
        >
          {loading ? 'Searching...' : 'Search Associates'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {searchResults && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Search completed: Found {searchResults.newsArticles} news articles and {searchResults.exaResults} web results
          </Typography>
          
          {searchQueries.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>
                Search queries used:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {searchQueries.map((query, index) => {
                  const getChipColor = (type: string) => {
                    switch (type) {
                      case 'comprehensive': return 'primary';
                      case 'news': return 'error';
                      case 'profile': return 'secondary';
                      case 'company': return 'info';
                      case 'industry': return 'success';
                      case 'social': return 'warning';
                      case 'achievements': return 'primary';
                      case 'speaking': return 'secondary';
                      case 'board': return 'info';
                      case 'investment': return 'success';
                      case 'background': return 'default';
                      case 'professional': return 'secondary';
                      case 'network': return 'warning';
                      case 'location': return 'error';
                      case 'role': return 'default';
                      case 'creative': return 'primary';
                      default: return 'default';
                    }
                  };

                  return (
                    <Chip
                      key={index}
                      label={query.query}
                      size="small"
                      variant="outlined"
                      color={getChipColor(query.type)}
                      title={query.reasoning || `${query.type} search`}
                      sx={{
                        fontSize: '0.7rem',
                        height: 20,
                        '& .MuiChip-label': {
                          px: 1,
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {summary && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" color="text.secondary">
            {summary}
          </Typography>
        </Box>
      )}

      {associates.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              Professional Connections ({associates.length})
            </Typography>
            <IconButton
              onClick={() => setExpanded(!expanded)}
              size="small"
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={expanded}>
            <List>
              {associates.map((associate, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      py: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <PersonIcon color="primary" />
                      </ListItemIcon>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {associate.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          {associate.title && (
                            <Typography variant="body2" color="text.secondary">
                              {associate.title}
                            </Typography>
                          )}
                          {associate.company && (
                            <>
                              <Typography variant="body2" color="text.secondary">
                                at
                              </Typography>
                              <Typography variant="body2" color="text.secondary" fontWeight="medium">
                                {associate.company}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                      <Chip
                        icon={getConfidenceIcon(associate.confidence)}
                        label={associate.confidence}
                        color={getConfidenceColor(associate.confidence)}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    <Box sx={{ ml: 5, width: '100%' }}>
                      {associate.relationship && (
                        <Box sx={{ mb: 1 }}>
                          <Chip
                            label={associate.relationship}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      )}

                      {associate.context && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {associate.context}
                        </Typography>
                      )}

                      {associate.source && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <LinkIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            Source: {associate.source}
                          </Typography>
                          {associate.sourceUrl && (
                            <Typography variant="caption">
                              <a 
                                href={associate.sourceUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                  color: '#1976d2', 
                                  textDecoration: 'underline',
                                  marginLeft: '4px'
                                }}
                              >
                                View Source
                              </a>
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </ListItem>
                  {index < associates.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Collapse>
        </Box>
      )}

      {hasSearched && associates.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            No known associates found in search results
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try searching again or check if the contact has sufficient online presence
          </Typography>
        </Box>
      )}

      {!hasSearched && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Search for professional connections and known associates
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will search news articles and web sources for information about {contactName}'s professional network
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
